import { isEmpty } from 'lodash/fp';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NoItemResult from '../../../components/results/NoItemResult';
import GroupedBarChart from './Charts/GroupedBarChart';
import { useDashboardContext } from './DashboardContext';
import usePopularVariantApplicationData from './data/usePopularVariantApplicationData';
import { FullScreenWrapper } from './shared/FullScreenWrapper';
import { type ChartDataProps, type Datum } from './shared/typings';
import useLegendSelectedConfig from './shared/useLegendSelectedConfig';
import useSeriesColors from './shared/useSeriesColors';
import useSeriesFields from './shared/useSeriesField';

export const PopularApplicationVariantChartViewID = 'popularApplicationVariantChart';

const PopularApplicationVariantChartContent = ({ data, t, noData }: ChartDataProps) => {
    const { currentMonth, lastMonth } = useSeriesFields();
    const legendSelected = useLegendSelectedConfig({ currentMonth, lastMonth });
    const color = useSeriesColors();

    if (noData) {
        return <NoItemResult subTitle={t('dashboard:noData.message')} />;
    }

    return (
        <GroupedBarChart
            {...t('dashboard:common.chart', { returnObjects: true })}
            color={color}
            data={data}
            visible={legendSelected}
            xField="noOfSubmitted"
            yField="variant"
        />
    );
};

const PopularApplicationVariantChart = () => {
    const { t } = useTranslation(['dashboard']);
    const { isLoading, error, generateData } = usePopularVariantApplicationData();
    const [data, setData] = useState<Datum | null>(null);

    useEffect(() => {
        generateData(result => setData(result));
    }, [generateData, setData]);

    const noData = useMemo(() => (data ? Object.values(data).every(isEmpty) : true), [data]);

    const { setVisible } = useDashboardContext();

    useEffect(() => {
        setVisible({ [PopularApplicationVariantChartViewID]: !error });
    }, [error, setVisible]);

    if (error) {
        return null;
    }

    return (
        <FullScreenWrapper
            disabledFullScreen={noData}
            height={585}
            isLoading={isLoading}
            title={t('dashboard:popularApplicationVariantChart.title')}
        >
            <PopularApplicationVariantChartContent data={data} noData={noData} t={t} />
        </FullScreenWrapper>
    );
};

export default PopularApplicationVariantChart;
